import { differenceInCalendarDays, isPast } from "date-fns";
import { type FC, Suspense, lazy, useEffect, useMemo, useState } from "react";

import formatDistanceToNow from "../../../i18n/formatDistanceToNow";

import { showPageDisabled } from "./groups/helpers";
import { getLateSubmissionDueDate, isInLateSubmissionWindow } from "./helpers";
import {
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE,
  PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW,
} from "./interaction-mode/helpers";

import { PageType } from "components/server-types";
import { formatLocalePattern, relativeDayAndTimeFormat } from "i18n/helpers";
import { t } from "i18n/i18n";
import { Chip, ChipProps } from "mds/components/Chip";
import { IconText } from "mds/components/IconText";
import { Popover, PopoverButton, PopoverPanel } from "mds/components/Popover";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CalendarIcon, ChevronDownIcon, LoadingDotsIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  selectCurrentSection,
  selectCurrentSectionIsLinked,
  selectIsEditModeEnabled,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { isSharedClassPage, isStaticPage } from "utils/page-utils";

const DueDateForm = lazy(() => import("components/forms/DueDateForm"));

type DueDateButtonProps = {
  pageId: string;
  readOnly?: boolean;
  kind?: "page" | "list-item";
  shortText?: boolean;
  hideIcon?: boolean;
  clickStopPropagation?: boolean;
};

const UPDATE_DISTANCE_INTERVAL = 30000;

type DisplayProps = {
  dueDateString: string;
  shortDueDateString?: string;
  dueDateTitle: string;
  isWithinLateWindow?: boolean;
};

const defaultLateSubmissionDisplayInfo = (
  lateDueDate: Date | null | undefined,
  distance: string,
): DisplayProps => {
  const dueDateTitle = lateDueDate
    ? t(`due_date.tooltips.accepting_late_submissions_date`, {
        date: formatLocalePattern(lateDueDate, "common.formats.month_day_time"),
      })
    : t(`due_date.tooltips.accepting_late_submissions_indefinite`, { distance });

  return {
    dueDateString: t("due_date.accepting_late_submissions"),
    shortDueDateString: t("due_date.accepting_late_submissions_short"),
    dueDateTitle,
  };
};

const defaultDisplayInfo = (dueDate: Date | null | undefined, distance: string): DisplayProps => {
  const daysDifference = dueDate ? Math.abs(differenceInCalendarDays(dueDate, new Date())) : 0;
  const shouldShowDistance = daysDifference <= 7;

  const dueDateString = dueDate
    ? shouldShowDistance
      ? t("due_date.due_with_distance", {
          date: dueDate,
          distance,
        })
      : t("due_date.due_without_distance", {
          date: dueDate,
        })
    : t("due_date.title");

  return {
    dueDateString,
    shortDueDateString: dueDate ? relativeDayAndTimeFormat(dueDate) : t("due_date.title"),
    dueDateTitle: dueDateString,
  };
};

const getDueDateDisplayInfo = (
  page: Pick<PageType, "due_at" | "interaction_mode" | "late_submission_interval"> | null,
  dueDate: Date | undefined,
  distance: string,
  shortText: boolean,
): DisplayProps => {
  const lateDueDate = dueDate ? (getLateSubmissionDueDate(page) ?? undefined) : undefined;
  const pastLateDueDate = lateDueDate && isPast(lateDueDate);
  const isWithinLateWindow =
    isStaticPage(page) || isSharedClassPage(page) ? false : isInLateSubmissionWindow(page);

  const {
    dueDateString,
    shortDueDateString,
    dueDateTitle: baseDueDateTitle,
  } = isWithinLateWindow
    ? defaultLateSubmissionDisplayInfo(lateDueDate, distance)
    : defaultDisplayInfo(dueDate, distance);

  const dueDateTitle = pastLateDueDate
    ? t("due_date.tooltips.accepted_late_submissions", { distance })
    : baseDueDateTitle;

  return {
    dueDateString: shortText && dueDate ? relativeDayAndTimeFormat(dueDate) : dueDateString,
    shortDueDateString,
    dueDateTitle,
    isWithinLateWindow,
  };
};

const ChipButton: FC<Omit<ChipProps<"button">, "as">> = (props) => <Chip as="button" {...props} />;

export const DueDateButton: FC<DueDateButtonProps> = ({
  pageId,
  readOnly,
  kind = "page",
  shortText,
  hideIcon,
  clickStopPropagation,
}) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    [
      "category",
      "due_at",
      "released_at",
      "interaction_mode",
      "late_submission_interval",
      "scheduled_release_at",
      "should_lock_at_due_date",
    ],
  );
  const dueDate = useMemo(() => (page?.due_at ? new Date(page.due_at) : undefined), [page?.due_at]);
  const scheduledReleaseAt = useMemo(
    () => (page?.scheduled_release_at ? new Date(page.scheduled_release_at) : undefined),
    [page?.scheduled_release_at],
  );
  const pageIsReleased = Boolean(page?.released_at);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  // Should always be editable in linked sections
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled) || isInLinkedSection;
  const section = useObjectSelector(selectCurrentSection, ["default_late_submission_interval"]);
  const pageSupportsLateSubmissions = !isStaticPage(page) && !isSharedClassPage(page);
  const { canAuthorSection } = useCapabilities();
  const isListItem = kind === "list-item";
  const showDisabled = isListItem && showPageDisabled(page);

  const [distance, setDistance] = useState<string>(
    dueDate ? formatDistanceToNow(dueDate, { addSuffix: true }) : "",
  );

  const { dueDateString, shortDueDateString, dueDateTitle, isWithinLateWindow } = useMemo(
    () => getDueDateDisplayInfo(page, dueDate, distance, shortText ?? false),
    [page, dueDate, distance, shortText],
  );

  useEffect(() => {
    if (dueDate) {
      setDistance(formatDistanceToNow(dueDate, { addSuffix: true }));

      const intervalId = setInterval(() => {
        // update `distance` string every 30s
        setDistance(formatDistanceToNow(dueDate, { addSuffix: true }));
      }, UPDATE_DISTANCE_INTERVAL);

      return () => clearInterval(intervalId);
    }
  }, [dueDate]);

  if (!page) {
    return null;
  }

  if (!readOnly && canAuthorSection && isEditModeEnabled) {
    return (
      <Popover className="relative">
        {isListItem ? (
          <PopoverButton
            as={SquareButton}
            kind={isWithinLateWindow ? "warning" : "tertiary"}
            size="xs"
            title={dueDateTitle}
          >
            <IconText iconEnd={<ChevronDownIcon />} text={dueDateString} />
          </PopoverButton>
        ) : (
          <PopoverButton
            as={ChipButton}
            kind={isWithinLateWindow ? "orange" : "white"}
            title={dueDateTitle}
          >
            <IconText
              containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
              containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
              iconEnd={<ChevronDownIcon />}
              iconStart={hideIcon ? undefined : <CalendarIcon />}
              shortText={shortDueDateString}
              text={dueDateString}
            />
          </PopoverButton>
        )}

        <PopoverPanel
          anchor="bottom"
          className="!z-[105]"
          onClick={clickStopPropagation ? (e) => e.stopPropagation() : undefined}
        >
          {({ close }) => (
            <>
              {pageIsReleased && (
                <div className="w-80 rounded border-orange bg-orange-tint-90 p-2">
                  {dueDate && isPast(dueDate)
                    ? t("due_date.reopen_submissions_warning")
                    : t("due_date.page_released_warning")}
                </div>
              )}

              <Suspense fallback={<LoadingDotsIcon />}>
                <DueDateForm
                  close={close}
                  date={dueDate}
                  lateInterval={page.late_submission_interval ?? undefined}
                  lockAtDueDate={page.should_lock_at_due_date}
                  pageId={pageId}
                  pageSupportsLateSubmissions={pageSupportsLateSubmissions}
                  scheduledReleaseAt={scheduledReleaseAt}
                  sectionDefaultLateInterval={section?.default_late_submission_interval}
                />
              </Suspense>
            </>
          )}
        </PopoverPanel>
      </Popover>
    );
  }

  if (dueDate) {
    const text = <span title={dueDateTitle}>{dueDateString}</span>;

    return isListItem ? (
      <SquareButton
        className="cursor-default"
        disabled={showDisabled}
        kind={isWithinLateWindow ? "warning" : "tertiary"}
        size="xs"
        title={dueDateTitle}
      >
        {text}
      </SquareButton>
    ) : (
      <Chip kind={isWithinLateWindow ? "orange" : "white"}>
        <IconText
          containerQueryClassNameHide={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_HIDE}
          containerQueryClassNameShow={PAGE_METADATA_CONTAINER_QUERY_CLASS_NAME_SHOW}
          iconStart={hideIcon ? undefined : <CalendarIcon />}
          shortText={shortDueDateString ?? undefined}
          text={dueDateString}
        />
      </Chip>
    );
  }

  return null;
};

DueDateButton.displayName = "DueDateButton";
